import { useMemo, useState, type MouseEvent } from "react";
import Button from "@scripts/react/components/Button";
import TextInput from "@scripts/react/components/TextInput";
import { SidebarActions } from "@rapid/components/layout/SidebarActions";
import { SidebarHeader } from "@rapid/components/layout/SidebarHeader";
import { useCoreApp } from "@rapid/contexts/CoreAppContext";
import { useFrame } from "@rapid/contexts/FrameContext";
import { useModel } from "@rapid/contexts/ModelContext";
import { useCurrentMedia, useSyncPromptLabels } from "@rapid/store";
import { useFlow } from "@rapid/contexts/FlowContext";
import { useIsGeneratingPredictions } from "@rapid/hooks/useIsGeneratingPredictions";
import { useRunInference } from "@rapid/hooks/useRunInference";
import { getLabelColor } from "@rapid/components/annotation/colors";
import { DEFAULT_CONFIDENCE_THRESHOLD } from "@rapid/utils/predictions";
import { useReviewTab } from "./ReviewContext";
import { ReviewTab } from "./ReviewTabs";

function CompactPromptLabels({
    labels,
    promptLabels,
    selectedLabel,
    onSelectLabel
}: {
    labels: string[];
    promptLabels: Map<string, { customColor?: string }>;
    selectedLabel: string | null;
    onSelectLabel: (label: string) => void;
}) {
    if (labels.length === 0) return null;

    return (
        <div className="flex flex-col gap-2">
            <div className="text-xs font-medium uppercase tracking-wide text-tertiary">
                Prompt Labels
            </div>
            <div className="flex flex-col gap-1.5">
                {labels.map((label) => (
                    <div
                        key={label}
                        className="flex cursor-pointer items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-secondary transition hover:border-gray-300 hover:bg-gray-50 data-[selected=true]:border-brand data-[selected=true]:bg-tint data-[selected=true]:text-brand"
                        data-selected={selectedLabel === label}
                        onClick={() => onSelectLabel(label)}
                    >
                        <span
                            className="h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: getLabelColor(promptLabels, label) }}
                        />
                        <span className="min-w-0 truncate">{label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

export const ReviewEditSidebarContent = () => {
    const {
        annotationsForFileAndFrameId,
        promptLabels,
        textPromptStr,
        textPrompts,
        setTextPromptStr
    } = useModel();
    const syncPromptLabels = useSyncPromptLabels();
    const currentMedia = useCurrentMedia();
    const { isExtractingWithAI } = useFlow();
    const isGeneratingPredictions = useIsGeneratingPredictions();
    const { runInference } = useRunInference();
    const { activeTab, updateModelEdits, cancelModelEdits } = useReviewTab();
    const { selectedObject, previouslySelectedObject, setPreviouslySelectedObject } = useCoreApp();
    const { setSelectedAnnotationId } = useFrame();
    const [isFindingObject, setIsFindingObject] = useState(false);

    const promptLabelNames = useMemo(() => Array.from(promptLabels.keys()), [promptLabels]);

    const handleFindObject = async (event: MouseEvent) => {
        event.preventDefault();
        if (!currentMedia?.file || textPrompts.length === 0 || isFindingObject) return;
        if (currentMedia.file.isVideo && !currentMedia.frame) return;

        const mediaItemToUse = currentMedia.file.isVideo ? currentMedia.frame! : currentMedia.file;
        const extractedFrameMeta =
            currentMedia.file.isVideo && currentMedia.frame
                ? {
                      videoId: currentMedia.file.id,
                      segment:
                          currentMedia.frame.metadata?.segment ?? currentMedia.frameSegment ?? 0
                  }
                : undefined;

        setIsFindingObject(true);
        try {
            await runInference({
                mediaItem: mediaItemToUse,
                extractedFrameMeta,
                annotationsForFileAndFrameId,
                isDraft: true,
                sam3Labels: textPrompts,
                owlv2Labels: textPrompts,
                sam3ConfidenceThreshold:
                    currentMedia.file.confidenceThreshold ?? DEFAULT_CONFIDENCE_THRESHOLD
            });

            syncPromptLabels(Array.from(new Set([...promptLabelNames, ...textPrompts])));
            setPreviouslySelectedObject(textPrompts[0] ?? null);
            setTextPromptStr("");
        } finally {
            setIsFindingObject(false);
        }
    };

    const isFindObjectDisabled =
        textPrompts.length === 0 ||
        isFindingObject ||
        isGeneratingPredictions ||
        isExtractingWithAI;

    const handleSelectLabel = (label: string) => {
        setSelectedAnnotationId(null);
        setPreviouslySelectedObject(label);
    };

    return (
        <>
            <SidebarHeader
                title={
                    activeTab === ReviewTab.MyAnnotations ? "Edit Annotations" : "Edit Predictions"
                }
            />
            <div className="content">
                <div className="flex flex-col gap-2">
                    <TextInput
                        placeholder="Find object"
                        disabled={isGeneratingPredictions || isExtractingWithAI || isFindingObject}
                        value={textPromptStr}
                        onChange={setTextPromptStr}
                        fullWidth
                    />
                    <Button
                        type="tertiary"
                        className="border-brand"
                        disabled={isFindObjectDisabled}
                        onClick={handleFindObject}
                    >
                        {isFindingObject ? "Finding..." : "Find Object"}
                    </Button>
                </div>
                <CompactPromptLabels
                    labels={promptLabelNames}
                    promptLabels={promptLabels}
                    selectedLabel={selectedObject ?? previouslySelectedObject}
                    onSelectLabel={handleSelectLabel}
                />
            </div>
            <SidebarActions>
                <Button type="primary" onClick={updateModelEdits}>
                    <i className="far fa-check" />
                    Update Model
                </Button>
                <Button type="secondary" onClick={cancelModelEdits}>
                    <i className="far fa-xmark" />
                    Cancel Edits
                </Button>
            </SidebarActions>
        </>
    );
};
